import { Box, colors, Flex, Link, Text, VStack } from "@biblioteksentralen/react";
import { Publication } from "@libry-content/types";
import { ReactNode, SVGProps } from "react";
import { getPublicationUrl } from "../../../../utils/getPublicationUrl";
import { useTranslation } from "../../../../utils/hooks/useTranslation";
import { GuessState, PublicationTag } from "../types";
import { useCommonData } from "../../../layout/CommonDataProvider";
import { ItemCoverImage } from "../../../../searchAndCollection/components/ItemCoverImage";

export type GuessStateMap = Record<PublicationTag["type"], GuessState>;

const QuestionmarkSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg width={props.width} height={props.height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="Group">
      <path
        id="Vector"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M12 18.75C12.3978 18.75 12.7794 18.908 13.0607 19.1893C13.342 19.4706 13.5 19.8522 13.5 20.25C13.5 20.6478 13.342 21.0294 13.0607 21.3107C12.7794 21.592 12.3978 21.75 12 21.75C11.6022 21.75 11.2206 21.592 10.9393 21.3107C10.658 21.0294 10.5 20.6478 10.5 20.25C10.5 19.8522 10.658 19.4706 10.9393 19.1893C11.2206 18.908 11.6022 18.75 12 18.75ZM12.005 2.75C15.173 2.75 17.76 5.17 17.76 8.18C17.76 10.949 15.571 13.218 12.756 13.564L12.755 16.37C12.7549 16.56 12.6828 16.7429 12.553 16.8818C12.4233 17.0206 12.2457 17.1051 12.0561 17.118C11.8665 17.131 11.6791 17.0715 11.5317 16.9516C11.3843 16.8316 11.2879 16.6602 11.262 16.472L11.255 16.37V12.86C11.255 12.6611 11.334 12.4703 11.4747 12.3297C11.6153 12.189 11.8061 12.11 12.005 12.11C14.365 12.11 16.26 10.339 16.26 8.18C16.26 6.021 14.366 4.25 12.005 4.25C10.7 4.25 9.496 4.795 8.695 5.712C8.0892 6.39167 7.75306 7.26954 7.75 8.18C7.75 8.37891 7.67098 8.56968 7.53033 8.71033C7.38968 8.85098 7.19891 8.93 7 8.93C6.80109 8.93 6.61032 8.85098 6.46967 8.71033C6.32902 8.56968 6.25 8.37891 6.25 8.18C6.25 6.9 6.721 5.689 7.565 4.724C8.653 3.482 10.268 2.75 12.005 2.75Z"
        fill="black"
      />
    </g>
  </svg>
);

const HideCoverImage = ({ hidden, children }: { hidden: boolean; children?: ReactNode }) => (
  <Box position="relative">
    <Box opacity={hidden ? "0" : "1"} aria-hidden={hidden ? true : false}>
      {children}
    </Box>
    {hidden && (
      <Flex
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        background={colors.lightGreen}
        alignItems="center"
        justifyContent="center"
        borderRadius="sm"
      >
        <QuestionmarkSVG width="3.5rem" height="3.5rem" />
      </Flex>
    )}
  </Box>
);

const EmptyCoverImage = ({
  guessedCorrectly,
  author,
  title,
}: {
  guessedCorrectly: boolean;
  author?: string;
  title?: string;
}) => {
  return (
    <VStack
      borderRadius="sm"
      minHeight={{ base: "6rem", md: "8rem" }}
      justifyContent="center"
      alignItems="center"
      padding={{ base: "0.25rem", md: "1rem" }}
      background={guessedCorrectly ? colors.accentGreen : colors.lightGreen}
      color={guessedCorrectly ? colors.lightGreen : colors.accentGreen}
    >
      {guessedCorrectly && (
        <VStack>
          <Text textAlign="center" fontSize="xs" marginTop="1rem" fontWeight={600}>
            {author}
          </Text>
          <Text textAlign="center" fontSize={{ base: "xs", md: "sm" }} fontWeight={600}>
            {title}
          </Text>
        </VStack>
      )}
    </VStack>
  );
};

export const GuessAuthorCoverImage = ({
  publication,
  guessState,
}: {
  publication: Publication;
  guessState?: GuessStateMap;
}) => {
  const { title, author } = publication;
  const { t } = useTranslation();
  const { site } = useCommonData();
  const publicationUrl = getPublicationUrl({ publication, site });
  const guessedCorrectly = Object.values(guessState ?? {}).includes("correct");

  if (publicationUrl) {
    return (
      <HideCoverImage hidden={!guessedCorrectly}>
        <Link href={publicationUrl} aria-label={t('Se "{tittel}" i Biblioteksystemet', { tittel: title ?? "tittel" })}>
          {publication.image ? (
            <ItemCoverImage
              resolution={500}
              item={publication}
              borderRadius="sm"
              boxShadow="none"
              height="fit-content"
            />
          ) : (
            <EmptyCoverImage guessedCorrectly={guessedCorrectly} author={author?.name} title={title} />
          )}
        </Link>
      </HideCoverImage>
    );
  }

  return (
    <HideCoverImage hidden={!guessedCorrectly}>
      {publication.image ? (
        <ItemCoverImage resolution={500} item={publication} borderRadius="sm" boxShadow="none" height="fit-content" />
      ) : (
        <EmptyCoverImage guessedCorrectly={guessedCorrectly} author={author?.name} title={title} />
      )}
    </HideCoverImage>
  );
};
